import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { biometricsSupported, enableBiometrics } from "@/lib/webauthn";
import api from "@/lib/api";
import { Fingerprint, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function BiometricPrompt() {
  const { user, refreshUser } = useAuth();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !user.email || !biometricsSupported()) return;
    if (user.biometric_enabled) return;
    const key = `bio_prompt_dismissed_${user.email}`;
    if (localStorage.getItem(key)) return;
    let cancelled = false;
    api.get(`/webauthn/available?email=${encodeURIComponent(user.email)}`)
      .then((r) => { if (!cancelled && !r.data.has_credentials) setShow(true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  const dismiss = () => {
    if (user?.email) localStorage.setItem(`bio_prompt_dismissed_${user.email}`, "1");
    setShow(false);
  };

  const enable = async () => {
    setLoading(true);
    try {
      await enableBiometrics(user);
      toast.success("Biometric login enabled — next time just use your fingerprint or Face ID.");
      if (refreshUser) await refreshUser();
      dismiss();
    } catch (e) {
      toast.error(e.message || "Could not enable biometrics. You can set it up later.");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-6" data-testid="biometric-prompt">
      <div className="relative bg-card border border-border max-w-md w-full p-8 rounded-sm">
        <button onClick={dismiss} data-testid="biometric-prompt-close" className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
        <div className="w-14 h-14 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center mb-5">
          <Fingerprint className="w-7 h-7 text-primary" />
        </div>
        <h2 className="font-serif text-3xl mb-2">Sign in faster next time</h2>
        <p className="text-muted-foreground text-sm leading-relaxed mb-6">
          Add your fingerprint, Face ID or device passkey to <span className="text-foreground">{user?.email}</span>.
          It stays on your device — we never see your biometrics — and lets you skip your password on future logins.
        </p>
        <div className="flex flex-col gap-3">
          <button onClick={enable} disabled={loading} data-testid="biometric-prompt-enable"
            className="flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm text-sm font-medium hover:-translate-y-[1px] transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />} Enable biometric login
          </button>
          <button onClick={dismiss} data-testid="biometric-prompt-skip"
            className="text-xs uppercase tracking-widest font-mono text-muted-foreground hover:text-foreground">
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
